// eslint-disable-next-line @typescript-eslint/no-explicit-any
let Sentry: any = null;
let initialized = false;

interface BrowserEvent {
  request?: { url?: string; query_string?: string | Record<string, string>; cookies?: unknown };
  breadcrumbs?: { data?: Record<string, unknown> }[];
}

function stripQuery(url: string | undefined): string | undefined {
  if (!url) return url;
  try {
    const u = new URL(url, window.location.origin);
    u.search = '';
    return u.toString();
  } catch {
    return url.split('?')[0];
  }
}

function scrubBrowserEvent(event: BrowserEvent): BrowserEvent {
  if (event.request) {
    if (event.request.url) event.request.url = stripQuery(event.request.url);
    if (event.request.query_string !== undefined) event.request.query_string = '[REDACTED]';
    if (event.request.cookies !== undefined) event.request.cookies = '[REDACTED]';
  }
  if (event.breadcrumbs) {
    for (const crumb of event.breadcrumbs) {
      if (crumb.data && typeof crumb.data.url === 'string') {
        crumb.data.url = stripQuery(crumb.data.url);
      }
    }
  }
  return event;
}

export async function initSentry(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  try {
    // Build the specifier at runtime so Vite's static import analysis
    // doesn't fail when @sentry/react is not installed.
    const pkg = ['@sentry', 'react'].join('/');
    Sentry = await import(/* @vite-ignore */ pkg);
  } catch {
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Mask everything in session replays. Authenticated pages render webhook
      // bodies (third-party PII), email addresses, and API keys in the DOM —
      // none of which we can ship to Sentry without disclosing the processing.
      Sentry.replayIntegration({
        maskAllInputs: true,
        maskAllText: true,
        blockAllMedia: true,
        networkDetailAllowUrls: [],
      }),
    ],
    tracesSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    sendDefaultPii: false,
    beforeSend: scrubBrowserEvent,
    beforeSendTransaction: scrubBrowserEvent,
  });

  initialized = true;
}

export function captureException(error: unknown): void {
  if (initialized && Sentry) {
    Sentry.captureException(error);
  }
}
